import React, { useState, useEffect } from "react";
import { Card } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { apiClient } from '../api/ApiClient';
import useFastPhotos from '../fastPhotos/useFastPhotos';
import Badge from "react-bootstrap/Badge";
import { Trash2 } from 'lucide-react';
import './BlockedMembersComponent.css';
import PaginationComponent from "../pagingResults/PaginationComponent";

const BlockedMembersComponent = () => {
  const navigate = useNavigate();
  const [blockedMembers, setBlockedMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalElements, setTotalElements] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [currentPage, setCurrentPage] = useState(0);
  
  const { fastPhotos, loading: fastPhotosLoading, error: fastPhotosError } = useFastPhotos();

  const passMemberTypeId = 1;

  // Function to record profile view
  const recordProfileView = async (memberId) => {
    try {
      console.log('Recording view for profile ID:', memberId);
      
      const response = await apiClient.post('/rest/recordView', {
        viewedMemberId: memberId
      });
      
      console.log('View recorded response:', response.data);
    } catch (error) {
      console.error('Error recording profile view:', error);
    }
  };

  // Handle click on a blocked member profile - no view recording
  const handleBlockedMemberClick = (e, memberId) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    if (token) {
      // No recording view for blocked members
      navigate(`/profile/view/1/${memberId}`);
    } else {
      navigate('/login');
    }
  };

  // Handle click on a close match profile
  const handleCloseMatchClick = (e, photo) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    if (token) {
      // Record the profile view
      recordProfileView(photo[0]);
      // Navigate to the profile page
      navigate(`/profile/view/1/${photo[0]}`);
    } else {
      navigate('/login');
    }
  };

  const fetchBlockedMembers = async () => {
    setLoading(true);
    try {
      // Use the updated paginated endpoint
      const response = await apiClient.get(`/rest/blocked?page=${currentPage}&size=${itemsPerPage}&memberTypeId=${passMemberTypeId}`);
      
      if (response.status === 200) {
        console.log('Blocked members response:', response.data);
        
        // Handle Spring Boot Page object
        if (response.data.content) {
          // If the response is a Page object
          setBlockedMembers(response.data.content);
          setTotalElements(response.data.totalElements);
        } else {
          // Fallback for backward compatibility
          const validData = response.data.filter(item => Array.isArray(item) && item.length > 1);
          setBlockedMembers(validData);
          setTotalElements(validData.length);
        }
      } else {
        setError('Failed to fetch blocked members');
      }
    } catch (error) {
      console.error('Error fetching blocked members:', error);
      setError('Error fetching blocked members. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('Fetching blocked members - Page:', currentPage, 'Size:', itemsPerPage);
    fetchBlockedMembers();
  }, [currentPage, itemsPerPage]);

  const formatDateTime = (dateString) => {
    const options = {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    };
    return new Date(dateString).toLocaleString(undefined, options);
  };

  const handleDelete = async (memberId) => {
    try {
      const blockedRequest = {
        blocked: [
          {
            name: "blocked_delete_list",
            val: memberId.toString()
          }
        ]
      };
  
      const response = await apiClient.post('/rest/deleteBlocked', blockedRequest);
      
      if (response.status === 200) {
        // Refresh the list after successful deletion
        fetchBlockedMembers();
      }
    } catch (error) {
      console.error('Error unblocking member:', error);
    }
  };

  return (
    <div className="container-1024">
      <div className="results-area">
        <h3>Blocked Members</h3>
        <div className="results-content">
          <div className="results-center-details">
            <div className="w-100 mb-3">
              <PaginationComponent
                totalItems={totalElements}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(newSize) => {
                  setItemsPerPage(newSize);
                  setCurrentPage(0); // Reset to first page when changing size
                }}
              />
            </div>

            {loading ? (
              <p>Loading blocked members...</p>
            ) : error ? (
              <p>{error}</p>
            ) : (
              <div className="card-grid">
                {blockedMembers.length > 0 ? (
                  blockedMembers.map((member, index) => (
                    <Card key={index} className="favorite-card position-relative">
                      <div className="delete-icon-wrapper"
                        style={{
                          position: 'absolute',
                          top: '10px',
                          right: '10px',
                          zIndex: 1,
                          cursor: 'pointer',
                          background: 'rgba(255, 255, 255, 0.8)',
                          borderRadius: '50%',
                          padding: '5px'
                        }}>
                        <Trash2
                          size={20}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDelete(member[0]);
                          }}
                        />
                      </div>
                      {/* Changed from Link to anchor with onClick handler but no view recording */}
                      <a 
                        href={`/profile/view/1/${member[0]}`} 
                        className="favorite-card-link"
                        onClick={(e) => handleBlockedMemberClick(e, member[0])}
                      >
                        <Card.Img 
                          variant="top" 
                          src={`https://pics.netfixup.com/${member[20]}/${member[19]}`}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "https://pics.netfixup.com/nfu_default_img/addNewUserPhoto.jpg";
                          }}
                        />
                      </a>
                      <Card.Body>
                        <Card.Title>{member[2]}</Card.Title>
                        <Card.Text>
                          <b>Age:</b> {member[3]}<br />
                          <b>Gender:</b> {member[5]}<br />
                          <b>Looking for:</b> {member[6]}<br />
                          <b>Location:</b> {member[15]}, {member[16]}<br />
                          <b>Online: </b> {member[23]}<br />
                          <b>Viewed: </b> {formatDateTime(member[22])}<br />
                        </Card.Text>
                      </Card.Body>
                    </Card>
                  ))
                ) : (
                  <p>No Blocked Members Found</p>
                )}
              </div>
            )}
          </div>

          <div className="results-right-sidebar">
            <h5>Close Matches</h5>
            {fastPhotosLoading ? (
              <p>Loading...</p>
            ) : fastPhotosError ? (
              <p>{fastPhotosError}</p>
            ) : fastPhotos.length > 0 ? (
              fastPhotos.map((photo, index) => (
                <Card key={index} className="fast-photo-card-side">
                  {/* Keep the view recording for Close Matches */}
                  <a 
                    href={`/profile/view/1/${photo[0]}`}
                    onClick={(e) => handleCloseMatchClick(e, photo)}
                  >
                    <Card.Img 
                      variant="top" 
                      src={`https://pics.netfixup.com/${photo[7]}${photo[6]}`}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://pics.netfixup.com/nfu_default_img/addNewUserPhoto.jpg";
                      }}
                    />
                  </a>
                  <Badge pill bg="light" text="dark">
                    {photo[2]}
                  </Badge>
                </Card>
              ))
            ) : (
              <p>No Fast Photos Found</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlockedMembersComponent;